import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MonitorPlay, Youtube, Instagram, Music2, Coffee, Smartphone, ExternalLink, ArrowRight } from "lucide-react";
import { api } from "../lib/apiClient";
import { Navbar } from "../components/Navbar";

const LINKS = [
  { label: "All My Links", handle: "linktr.ee/questmediahub", url: "https://linktr.ee/questmediahub", Icon: ExternalLink, accent: "#39E09B" },
  { label: "Q Fashion", handle: "YouTube · @Quest-Media-Hub", url: "http://www.youtube.com/@Quest-Media-Hub?sub_confirmation=1", Icon: Youtube, accent: "#FF2A2A" },
  { label: "Instagram", handle: "@q_fashionhubs", url: "https://www.instagram.com/q_fashionhubs", Icon: Instagram, accent: "#E1306C" },
  { label: "TikTok", handle: "@questmediahub", url: "https://www.tiktok.com/@questmediahub", Icon: Music2, accent: "#25F4EE" },
  { label: "Buy Me A Coffee", handle: "Support the network", url: "https://buymeacoffee.com/questmediao", Icon: Coffee, accent: "#FFDD00" },
  { label: "Quest App", handle: "quest.ihub.app", url: "http://quest.ihub.app/", Icon: Smartphone, accent: "#8B5CF6" },
];

export default function About() {
  const [channels, setChannels] = useState([]);
  const [counts, setCounts] = useState({});

  useEffect(() => {
    api.get("/channels").then((r) => setChannels(r.data)).catch(() => {});
    api.get("/sync/status").then((r) => setCounts(r.data.video_counts || {})).catch(() => {});
  }, []);

  const totalVideos = Object.values(counts).reduce((a, b) => a + b, 0);

  const stats = [
    { label: "Channels", value: channels.length || "—" },
    { label: "Videos", value: totalVideos ? `${totalVideos}+` : "—" },
    { label: "Cost To Watch", value: "Free" },
  ];

  return (
    <div className="min-h-screen bg-[#030305] text-white" data-testid="about-page">
      <Navbar />

      {/* Hero */}
      <div className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_20%_20%,#FF2A2A_0%,transparent_45%),radial-gradient(circle_at_80%_60%,#7C3AED_0%,transparent_45%)]" />
        <div className="relative max-w-[1100px] mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-16">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-2 mb-6">
              <MonitorPlay className="w-7 h-7 text-[#FF2A2A]" strokeWidth={2.5} />
              <span className="font-display font-900 text-2xl tracking-tighter uppercase">
                QTV<span className="text-[#FF2A2A]">Network</span>
              </span>
            </div>
            <p className="text-xs tracking-[0.25em] uppercase text-zinc-400 mb-4">About The Network</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter leading-[0.95] max-w-3xl">
              Streaming the world's best creators, all in one place.
            </h1>
            <p className="mt-6 text-lg text-zinc-300 max-w-2xl leading-relaxed">
              QTV Network is the streaming home of <span className="text-white font-semibold">Quest Media Hub</span> — a creator and distributor of educational entertainment media.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-14 md:py-20 space-y-16">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4" data-testid="about-stats">
          {stats.map((s) => (
            <div key={s.label} className="border border-white/10 rounded-md p-6 text-center bg-white/[0.02]">
              <p className="font-display text-3xl md:text-5xl tracking-tighter text-[#FF2A2A]">{s.value}</p>
              <p className="text-xs md:text-sm uppercase tracking-[0.15em] text-zinc-400 mt-2">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Description */}
        <section className="space-y-5 max-w-3xl">
          <h2 className="font-display text-2xl md:text-3xl uppercase tracking-tight">What We're About</h2>
          <p className="text-zinc-300 leading-relaxed">
            We bring together the most inspiring voices in travel, fitness, fashion, sports, music, RV life, the great outdoors, living abroad and more — and package them into a clean, Netflix-style experience you'll actually enjoy browsing. Every channel is hand-curated from real YouTube creators and refreshed daily, so there's always something new to watch.
          </p>
          <p className="text-zinc-300 leading-relaxed">
            No subscriptions. No paywalls. Just free, high-quality content organized the way you like it — rotating billboards, horizontal rows, cross-channel search, a personal watchlist, and even TikTok-style vertical Shorts feeds. Create a free account to save your favorites and pick up right where you left off.
          </p>
          <p className="text-zinc-300 leading-relaxed">
            QTV Network is more than a channel line-up — it's a growing media platform built by Quest Media Hub to entertain, educate and connect audiences everywhere.
          </p>
        </section>

        {/* Channels */}
        {channels.length > 0 && (
          <section className="space-y-5">
            <h2 className="font-display text-2xl md:text-3xl uppercase tracking-tight">Our Channels</h2>
            <div className="flex flex-wrap gap-3" data-testid="about-channels">
              {channels.map((c) => (
                <Link
                  key={c.slug}
                  to={`/channel/${c.slug}`}
                  data-testid={`about-channel-${c.slug}`}
                  className="group flex items-center gap-2 border border-white/15 hover:border-[#FF2A2A] bg-white/[0.03] hover:bg-white/[0.06] rounded-full pl-4 pr-3 py-2 transition-colors"
                >
                  <span className="text-sm text-zinc-200 group-hover:text-white">{c.name}</span>
                  <ArrowRight className="w-3.5 h-3.5 text-zinc-500 group-hover:text-[#FF2A2A] transition-colors" />
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Connect / Links */}
        <section className="space-y-6">
          <div>
            <h2 className="font-display text-2xl md:text-3xl uppercase tracking-tight">Connect With Us</h2>
            <p className="text-zinc-400 mt-2">Follow Quest Media Hub and explore everything we're building.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="about-links">
            {LINKS.map(({ label, handle, url, Icon, accent }) => (
              <a
                key={label}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`about-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className="group flex items-center gap-4 border border-white/10 hover:border-white/25 rounded-lg p-4 bg-white/[0.02] hover:bg-white/[0.05] transition-all hover:-translate-y-0.5"
              >
                <span
                  className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${accent}1A`, color: accent }}
                >
                  <Icon className="w-5 h-5" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-white">{label}</span>
                  <span className="block text-xs text-zinc-400 truncate">{handle}</span>
                </span>
                <ExternalLink className="w-4 h-4 text-zinc-600 group-hover:text-white ml-auto transition-colors" />
              </a>
            ))}
          </div>
        </section>

        {/* CTA */}
        <div className="border-t border-white/10 pt-12 text-center">
          <h3 className="font-display text-3xl md:text-4xl uppercase tracking-tight">Start Watching Free</h3>
          <p className="text-zinc-400 mt-3 mb-7">Dive into every channel — no subscription required.</p>
          <Link
            to="/channels"
            data-testid="about-cta-browse"
            className="inline-flex items-center gap-2 bg-[#FF2A2A] hover:bg-[#FF4D4D] text-white px-8 py-3.5 rounded-sm font-bold transition-colors"
          >
            Browse Channels <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
